import { createRootRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Layout } from '../components/Layout';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useOrganization, useClerk } from '@clerk/clerk-react';
import { useStore } from '../store';
import { send, waitForReady } from '../ws';
import { Kind, EvtType } from '@interlucent/organization-protocol';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ResumeUploadDialog } from '../components/ResumeUploadDialog';
import type { StoredUploadState } from '../types/storedUpload';
import { waitForAck } from '../utils/waitForAck';
import { AppProvider } from '../contexts/AppContext';
import { correlationId } from "@interlucent/ids";
import { logger } from '../log';
import type { UploadStartedInfo } from '../uploader';
import { initOrgConnection, setConnectionCallbacks, clearConnectionCallbacks } from '../wsConnection';

const log = logger('root');

export type NavSection = 'projects' | 'analytics' | 'documentation' | 'settings';

// Feature flag for onboarding flow
const isOnboardingEnabled = import.meta.env.VITE_FEATURE_ONBOARDING_ENABLED === 'true';

export const Route = createRootRoute({
  component: RootComponent,
});

// =============================================================================
// ClerkWsBridge — owns Clerk hooks + WS lifecycle effects.
// Renders null so Clerk-triggered re-renders don't cascade to the component tree.
// =============================================================================

function ClerkWsBridge({ setErrorMessage }: { setErrorMessage: (msg: string) => void }) {
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const { organization } = useOrganization();
  const clerk = useClerk();
  const clerkRef = useRef(clerk);
  clerkRef.current = clerk;
  const navigate = useNavigate();

  // Redirect users without an org to onboarding (only if feature enabled)
  useEffect(() => {
    if (!isOnboardingEnabled) return;
    if (getToken && !organization && !window.location.pathname.includes('/onboarding')) {
      log.info('No organization found, redirecting to onboarding');
      navigate({ to: '/onboarding' });
    }
  }, [organization, getToken, navigate]);

  // Admin org override from store
  const adminOrgOverride = useStore(s => s.adminOrgOverride);

  // Initialize the WebSocket singleton — survives component unmount/remount
  // Reconnects when the admin override changes (admin switching orgs)
  useEffect(() => {
    const targetOrg = adminOrgOverride || organization?.id;
    if (!targetOrg) {
      log.debug('No organization ID yet');
      return;
    }

    const tokenGetter = async () => {
      const t = await getTokenRef.current({ template: 'default' });
      if (!t) {
        log.error('No token available from Clerk');
        return null;
      }
      return t;
    };

    initOrgConnection(targetOrg, tokenGetter);
    // No cleanup — the singleton survives component unmounts.
    // Disposal happens only on explicit org change (handled by initOrgConnection).
  }, [organization?.id, adminOrgOverride]);

  // Register React-layer callbacks (navigation, error state)
  // Uses refs for clerk so the effect doesn't re-run on every Clerk internal refresh.
  useEffect(() => {
    setConnectionCallbacks({
      onAuthRequired: () => {
        log.info('Auth required — redirecting to Clerk sign-in');
        clerkRef.current.redirectToSignIn();
      },
      extraMessageHandler: (msg: any) => {
        // Handle initial connection ack - check requiresOnboarding (only if feature enabled)
        if (msg.kind === Kind.Ack && msg.payload?.requiresOnboarding !== undefined) {
          log.info(`Initial ack received, requiresOnboarding: ${msg.payload.requiresOnboarding}`);
          if (isOnboardingEnabled && msg.payload.requiresOnboarding && !window.location.pathname.includes('/onboarding')) {
            log.info('Onboarding required, redirecting to /onboarding');
            navigate({ to: '/onboarding' });
          }
        }

        // Handle onboarding completed event - redirect to dashboard
        if (msg.kind === Kind.Evt && msg.type === EvtType.OnboardingCompleted) {
          log.info('Onboarding completed event received');
          if (window.location.pathname.includes('/onboarding')) {
            navigate({ to: '/' });
          }
        }

        if (msg.kind === Kind.Err) {
          log.error('Error from server', msg);

          // Handle onboarding_required error specifically (only if feature enabled)
          if (isOnboardingEnabled && (msg.payload?.code === 'onboarding_required' || msg.code === 'onboarding_required')) {
            log.info('Onboarding required, redirecting to onboarding page');
            if (!window.location.pathname.includes('/onboarding')) {
              navigate({ to: '/onboarding' });
            }
          } else if (msg.payload?.code !== 'onboarding_required' && msg.code !== 'onboarding_required') {
            setErrorMessage(msg.message || msg.payload?.message || `Error: ${msg.code || msg.payload?.code}`);
          }
        }
      },
    });

    return () => {
      clearConnectionCallbacks();
    };
  }, [navigate, setErrorMessage]);

  return null;
}

// =============================================================================
// RootComponent — no Clerk hooks, only re-renders from its own state + Zustand.
// =============================================================================

function RootComponent() {
  const navigate = useNavigate();
  const isConnected = useStore(s => s.connectionState === 'connected');
  const wsEverConnected = useStore(s => s.wsEverConnected);
  const [errorMessage, setErrorMessage] = useState('');
  const [resumeUploadState, setResumeUploadState] = useState<StoredUploadState | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const handleUploadStarted = useCallback((info: UploadStartedInfo) => {
    log.info(`Upload started: ${info.uploadId}`);
    const prev = useStore.getState().uploadProgress;
    useStore.setState({
      uploadProgress: {
        ...prev,
        [info.uploadId]: {
          sent: 0,
          total: info.totalBytes,
          key: info.key,
          projectId: info.projectId,
          appId: info.appId,
          replaceVersionId: info.replaceVersionId,
        },
      },
    });
  }, []);

  const handleCancelUpload = (uploadId: string, key: string) => {
    const fileName = key.split('/').pop() || 'this file';
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Upload',
      message: `Cancel upload of ${fileName}?`,
      onConfirm: () => {
        const corr = correlationId();
        log.info('Sending cancel upload', { uploadId, key, corr });
        send('upload.abort', { uploadId, key }, corr);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handlePauseUpload = (uploadId: string) => {
    const corr = correlationId();
    log.info('Sending pause upload', { uploadId, corr });
    send('upload.pause', { uploadId }, corr);
  };

  const handleResumeUpload = async (uploadId: string) => {
    try {
      await waitForReady();
      const corr = correlationId();
      send('upload.status', { uploadId }, corr);

      const ack = await waitForAck<any>(corr);

      if (!ack.valid) {
        log.error(`Upload not found or expired: ${uploadId}`);
        alert('Upload not found or has expired');
        return;
      }

      const uploadState: StoredUploadState = {
        uploadId: ack.uploadId,
        key: ack.key || '',
        bucket: ack.bucket,
        filename: ack.filename,
        size: ack.size,
        contentType: ack.contentType,
        partSize: ack.partSize,
        projectId: ack.projectId,
        appId: ack.appId,
        appName: ack.appName,
        appSlugHint: ack.appSlugHint,
        checksum_sha256: ack.checksum_sha256,
        config_json: ack.config_json,
        gameEngine: ack.gameEngine,
        operatingSystem: ack.operatingSystem,
        executablePath: ack.executablePath,
        cmdArgs: ack.cmdArgs,
        environmentVariables: ack.environmentVariables,
        replaceVersionId: ack.replaceVersionId,
        completedParts: ack.completedParts || [],
        createdAt: ack.createdAt,
        lastUpdatedAt: ack.lastUpdatedAt,
        expiresAt: ack.expiresAt,
        state: ack.state,
        file: {
          name: ack.filename || '',
          size: ack.size || 0,
          lastModified: new Date(ack.createdAt || Date.now()).getTime(),
        },
      };

      setResumeUploadState(uploadState);
    } catch (error) {
      log.error('Failed to get upload status', error);
      alert('Failed to resume upload. Please try again.');
    }
  };

  const contextValue = useMemo(() => ({
    errorMessage,
    setErrorMessage,
    handleCancelUpload,
    handlePauseUpload,
    handleResumeUpload,
    handleUploadStarted,
  }), [errorMessage]);

  // Reactive pathname — re-renders when TanStack Router navigates
  const { pathname } = useLocation();

  // Check if this is a public route that should skip Layout
  const isPublicRoute =
    pathname.startsWith('/preview') ||
    pathname.startsWith('/check') ||
    pathname === '/terms' ||
    pathname === '/eula' ||
    pathname === '/privacy' ||
    pathname === '/network-connectivity-requirements';

  return (
    <AppProvider value={contextValue}>
      <ClerkWsBridge setErrorMessage={setErrorMessage} />

      {isPublicRoute ? (
        <Outlet />
      ) : (
        <Layout>
          <Outlet />
        </Layout>
      )}

      {resumeUploadState && (
        <ResumeUploadDialog
          uploadState={resumeUploadState}
          onClose={() => setResumeUploadState(null)}
          onUploadStarted={handleUploadStarted}
          onResumed={() => {}}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Breathing overlay — visible when WS has connected before but is currently down */}
      {wsEverConnected && !isConnected && !isPublicRoute && (
        <>
          <style>{`
            @keyframes breathe {
              0%, 100% { opacity: 0.07; }
              50% { opacity: 0.8; }
            }
          `}</style>
          <div
            aria-hidden="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'black',
              opacity: 0.07,
              pointerEvents: 'none',
              zIndex: 998,
              animation: 'breathe 4s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              touchEvents: 'none'
            }}
          />
        </>
      )}

      {import.meta.env.DEV && <TanStackRouterDevtools />}
    </AppProvider>
  );
}
